// =====================================================
// Swiss Finance OS - Dashboard Error Fallback
// Error fallback component for dashboard widgets
// =====================================================

import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, TrendingDown } from 'lucide-react';

interface DashboardErrorFallbackProps {
  error?: Error;
  onRetry?: () => void;
  title?: string;
  description?: string;
  variant?: 'default' | 'minimal' | 'detailed';
}

export function DashboardErrorFallback({
  error,
  onRetry,
  title = 'Data Unavailable',
  description = 'Unable to load dashboard data. Please try again.',
  variant = 'default'
}: DashboardErrorFallbackProps) {
  if (variant === 'minimal') {
    return (
      <div className="bg-card p-6 rounded-2xl border">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <TrendingDown className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{title}</p>
            {onRetry && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRetry}
                className="mt-2 h-7 text-xs"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'detailed') {
    return (
      <div className="bg-card p-6 rounded-2xl border border-destructive/20">
        <div className="flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-semibold text-brand-primary mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{description}</p>

            {process.env.NODE_ENV === 'development' && error && (
              <div className="mb-4 p-3 bg-destructive/10 rounded-lg">
                <p className="text-xs font-mono text-destructive">
                  {error.message}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              {onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  className="h-7 text-xs"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Try Again
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.reload()}
                className="h-7 text-xs"
              >
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-brand-primary mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground mb-6">{description}</p>

        {onRetry && (
          <Button
            variant="outline"
            onClick={onRetry}
            className="btn-swiss-secondary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
}

// Loading state component for dashboard widgets
export function DashboardLoadingFallback({
  title = 'Loading...',
  height = 'h-32'
}: {
  title?: string;
  height?: string;
}) {
  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className={`flex items-center justify-center ${height}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent mx-auto mb-3"></div>
          <p className="text-sm text-muted-foreground">{title}</p>
        </div>
      </div>
    </div>
  );
}

// Network error specific fallback
export function NetworkErrorFallback({
  onRetry,
  isOffline = false
}: {
  onRetry?: () => void;
  isOffline?: boolean;
}) {
  return (
    <DashboardErrorFallback
      title={isOffline ? 'No Internet Connection' : 'Network Error'}
      description={
        isOffline
          ? 'Check your internet connection and try again.'
          : 'Unable to connect to Swiss Finance OS servers. Please check your connection.'
      }
      onRetry={onRetry}
      variant="detailed"
    />
  );
}

export default DashboardErrorFallback;